import React from "react";
import Layout from "../Layout";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  MapPin,
  Phone,
  Smartphone,
  ExternalLink,
  Clock,
  UtensilsCrossed,
  ChefHat,
  Zap,
} from "lucide-react";
import { indexStore } from "../../../store/indexStore";
import BranchModel from "../../../Models/BranchModel";
import { Link } from "react-router-dom";
import { IMG_URl } from "../../../common/img_url";
import Iterator from "../../utils/Iterator";
import Visibility from "../../utils/Visibility";
import heroVideo from "../../../assets/intro.mp4";

function HomePage() {
  const { t } = useTranslation();
  const { branchesLoading, branches, lang, name, settings } = indexStore();

  return (
    <Layout title={t("HomePage.heroTitle")}>
      <div className="flex flex-col gap-8 md:gap-12">
        <div>
          <HeroSection t={t} />
          <AppDownloadSection t={t} lang={lang} />
        </div>
        <AboutSection t={t} />
        <FeaturesSection t={t} />
        <BranchesSection
          t={t}
          branches={branches}
          loading={branchesLoading}
          lang={lang}
          name={name}
          openingTime={settings?.opening_time}
          closingTime={settings?.closing_time}
        />
      </div>
    </Layout>
  );
}

// App Download Section
const AppDownloadSection = ({ t, lang }: any) => {
  return (
    <section className="px-4 md:px-8 lg:px-12 pt-8 md:pt-12 pb-12 md:pb-16 relative overflow-hidden">
      {/* Seamless background transition from hero - starts dark and fades to background */}
      <div className="absolute inset-0 bg-gradient-to-b from-primary/40 via-primary/10 to-background"></div>
      {/* Background decorations */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-primary/10 rounded-full blur-3xl opacity-30"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-primary/5 rounded-full blur-3xl opacity-30"></div>

      <div className="container mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto text-center space-y-6 md:space-y-8"
        >
          {/* Title */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
              {t("HomePage.appDownloadTitle")}
            </h2>
            <div className="w-24 h-1 bg-primary mx-auto rounded-full mb-4"></div>
            <p className="text-xl md:text-2xl text-primary font-semibold mb-2">
              {t("HomePage.appDownloadSubtitle")}
            </p>
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
              {t("HomePage.appDownloadDescription")}
            </p>
          </motion.div>

          {/* App Store Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          >
            <p className="text-sm md:text-base text-muted-foreground mb-2 sm:mb-0">
              {t("HomePage.availableOn")}:
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              {/* Google Play Store */}
              <motion.a
                href="https://play.google.com/store/apps/details?id=com.kreich.app"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05, y: -3 }}
                whileTap={{ scale: 0.95 }}
                className="inline-block"
              >
                <img
                  src="https://upload.wikimedia.org/wikipedia/commons/7/78/Google_Play_Store_badge_EN.svg"
                  alt="Get it on Google Play"
                  className="h-14 md:h-16 w-auto"
                />
              </motion.a>

              {/* Apple App Store */}
              <motion.a
                href="https://apps.apple.com/app/kriesh/id6759609288"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.05, y: -3 }}
                whileTap={{ scale: 0.95 }}
                className="inline-block"
              >
                <img
                  src="https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83&releaseDate=1288329600"
                  alt="Download on the App Store"
                  className="h-14 md:h-16 w-auto"
                />
              </motion.a>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default HomePage;

// Hero Section
const HeroSection = ({ t }: any) => {
  return (
    <section className="relative min-h-[70vh] md:min-h-[80vh] flex items-center justify-center overflow-hidden pb-0">
      {/* Video Background */}
      <div className="absolute inset-0 w-full h-full">
        <video
          autoPlay
          loop
          muted
          playsInline
          className="w-full h-full object-cover"
        >
          <source src={heroVideo} type="video/mp4" />
          {/* Fallback image if video doesn't load */}
          <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-primary/10 to-background"></div>
        </video>
        {/* Overlay for better text readability */}
        <div className="absolute inset-0 bg-black/40"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 md:px-8 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center max-w-3xl mx-auto space-y-4 md:space-y-6"
        >
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-4xl md:text-6xl lg:text-7xl font-bold leading-tight text-white drop-shadow-lg"
          >
            {t("HomePage.heroTitle")}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-lg md:text-xl lg:text-2xl text-white/90 leading-relaxed drop-shadow-md"
          >
            {t("HomePage.heroSubtitle")}
          </motion.p>
        </motion.div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = ({ t }: any) => {
  return (
    <section className="px-4 md:px-8 lg:px-12 py-12 md:py-16">
      <div className="container mx-auto max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center space-y-6"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground">
            {t("HomePage.aboutTitle")}
          </h2>
          <div className="w-24 h-1 bg-primary mx-auto rounded-full"></div>
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
            {t("HomePage.aboutDescription")}
          </p>
        </motion.div>
      </div>
    </section>
  );
};

// Features Section
const FeaturesSection = ({ t }: any) => {
  const features = [
    {
      icon: <UtensilsCrossed className="w-8 h-8 md:w-10 md:h-10" />,
      title: t("HomePage.freshFood"),
      description: t("HomePage.freshFoodDesc"),
    },
    {
      icon: <ChefHat className="w-8 h-8 md:w-10 md:h-10" />,
      title: t("HomePage.authenticTaste"),
      description: t("HomePage.authenticTasteDesc"),
    },
    {
      icon: <Zap className="w-8 h-8 md:w-10 md:h-10" />,
      title: t("HomePage.fastService"),
      description: t("HomePage.fastServiceDesc"),
    },
  ];

  return (
    <section className="px-4 md:px-8 lg:px-12 py-12 md:py-16 bg-gradient-to-br from-muted/50 via-muted/30 to-background">
      <div className="container mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            {t("HomePage.featuresTitle")}
          </h2>
          <div className="w-24 h-1 bg-primary mx-auto rounded-full"></div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 max-w-6xl mx-auto">
          <Iterator
            data={features}
            render={(feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -8, scale: 1.02 }}
                className="bg-card border-2 border-border rounded-2xl p-6 md:p-8 shadow-md hover:shadow-xl transition-all duration-300 text-center group"
              >
                <div className="flex justify-center mb-4">
                  <div className="p-4 bg-primary/10 rounded-2xl group-hover:bg-primary/20 transition-colors">
                    <div className="text-primary">{feature.icon}</div>
                  </div>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-foreground mb-3 group-hover:text-primary transition-colors">
                  {feature.title}
                </h3>
                <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            )}
          />
        </div>
      </div>
    </section>
  );
};

// Helper function to format time (from HH:MM:SS to HH:MM AM/PM)
const formatTime = (timeString: string | undefined): string => {
  if (!timeString || typeof timeString !== "string") return "";

  const safeTimeString = timeString; // TypeScript now knows it's string

  try {
    // Split time string (e.g., "09:00:00")
    const parts = safeTimeString.split(":");
    if (parts.length < 2) return safeTimeString;

    const hours = parts[0];
    if (!hours) return safeTimeString;

    const minutes = parts[1] || "00";
    const hour = parseInt(hours, 10);

    if (isNaN(hour)) return safeTimeString;

    // Convert to 12-hour format
    if (hour === 0) {
      return `12:${minutes} AM`;
    } else if (hour < 12) {
      return `${hour}:${minutes} AM`;
    } else if (hour === 12) {
      return `12:${minutes} PM`;
    } else {
      return `${hour - 12}:${minutes} PM`;
    }
  } catch (error) {
    return safeTimeString;
  }
};

// Branches Section
export const BranchesSection = ({
  t,
  branches,
  loading,
  lang,
  name,
  openingTime,
  closingTime,
}: {
  t: any;
  branches: BranchModel[];
  loading: boolean;
  lang: string;
  name: string;
  openingTime?: string;
  closingTime?: string;
}) => {
  const addressKey = name === "name_en" ? "address_en" : "address_ar";
  const branchNameKey = name === "name_en" ? "name_en" : "name_ar";

  // Format times
  const formattedOpeningTime = openingTime ? formatTime(openingTime) : "";
  const formattedClosingTime = closingTime ? formatTime(closingTime) : "";

  // Create working hours text
  const workingHoursText =
    openingTime && closingTime && formattedOpeningTime && formattedClosingTime
      ? `${formattedOpeningTime} - ${formattedClosingTime}`
      : t("HomePage.dailyHours");

  if (loading) {
    return (
      <section className="px-4 md:px-8 lg:px-12 py-12 md:py-16">
        <div className="container mx-auto">
          <div className="text-center mb-12 animate-pulse">
            <div className="h-12 w-64 bg-muted rounded-lg mx-auto mb-3"></div>
            <div className="h-4 w-48 bg-muted/60 rounded-full mx-auto"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-5xl mx-auto">
            {[1, 2].map((i) => (
              <div
                key={i}
                className="bg-gradient-to-br from-card to-background border-2 border-border rounded-2xl p-6 md:p-8 shadow-sm space-y-6 animate-pulse"
              >
                {/* Branch Image Skeleton */}
                <div className="w-full h-48 bg-muted/50 rounded-xl"></div>

                {/* Branch Name Skeleton */}
                <div className="h-8 w-2/3 bg-muted rounded-lg"></div>

                {/* Branch Info Skeletons */}
                <div className="space-y-4">
                  {/* Address Skeleton */}
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/20 border border-border/20">
                    <div className="w-5 h-5 rounded-full bg-muted/50 flex-shrink-0"></div>
                    <div className="flex-1 space-y-2">
                      <div className="h-3 w-16 bg-muted/50 rounded"></div>
                      <div className="h-4 w-5/6 bg-muted rounded"></div>
                    </div>
                  </div>

                  {/* Working Hours Skeleton */}
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/20 border border-border/20">
                    <div className="w-5 h-5 rounded-full bg-muted/50 flex-shrink-0"></div>
                    <div className="flex-1 space-y-2">
                      <div className="h-3 w-20 bg-muted/50 rounded"></div>
                      <div className="h-4 w-1/2 bg-muted rounded"></div>
                    </div>
                  </div>

                  {/* Phone Skeleton */}
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/20 border border-border/20">
                    <div className="w-5 h-5 rounded-full bg-muted/50 flex-shrink-0"></div>
                    <div className="flex-1 space-y-2">
                      <div className="h-3 w-16 bg-muted/50 rounded"></div>
                      <div className="h-4 w-1/3 bg-muted rounded"></div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="px-4 md:px-8 lg:px-12 py-12 md:py-16">
      <div className="container mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            {t("HomePage.branchesTitle")}
          </h2>
          <div className="w-24 h-1 bg-primary mx-auto rounded-full mb-2"></div>
          <p className="text-base md:text-lg text-muted-foreground">
            {t("HomePage.branchesSubtitle")}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 max-w-5xl mx-auto">
          <Iterator
            data={branches}
            render={(branch: BranchModel, index: number) => (
              <motion.div
                key={branch.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -5, scale: 1.02 }}
                className="bg-gradient-to-br from-card to-background border-2 border-border rounded-2xl p-6 md:p-8 shadow-md hover:shadow-xl transition-all duration-300 group"
              >
                {/* Branch Image */}
                <Visibility visible={!!branch.image}>
                  <div className="mb-6 rounded-xl overflow-hidden">
                    <img
                      src={`${IMG_URl}${branch.image}`}
                      alt={branch[branchNameKey as keyof BranchModel] as string}
                      className="w-full h-48 object-contain group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                </Visibility>

                {/* Branch Name */}
                <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-4 group-hover:text-primary transition-colors">
                  {branch[branchNameKey as keyof BranchModel] as string}
                </h3>

                {/* Branch Info */}
                <div className="space-y-3">
                  {/* Address */}
                  <Visibility
                    visible={!!branch[addressKey as keyof BranchModel]}
                  >
                    <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/40 border border-border/40 hover:border-primary/30 transition-all">
                      <MapPin className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-xs text-muted-foreground mb-1">
                          {t("HomePage.branchAddress")}
                        </p>
                        <p className="text-sm md:text-base font-medium text-foreground">
                          {branch[addressKey as keyof BranchModel] as string}
                        </p>
                      </div>
                      <Visibility visible={!!branch.address_link}>
                        <a
                          href={branch.address_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary hover:text-primary/80 transition-colors"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      </Visibility>
                    </div>
                  </Visibility>

                  {/* Google Map */}
                  <Visibility visible={!!(branch.latitude && branch.longitude)}>
                    <div className="mb-3 rounded-xl overflow-hidden border border-border/40">
                      <iframe
                        title={`${branch[branchNameKey as keyof BranchModel] as string} - ${t("HomePage.branchAddress")} - Map ${branch.id}`}
                        width="100%"
                        height="250"
                        style={{ border: 0 }}
                        loading="lazy"
                        allowFullScreen
                        referrerPolicy="no-referrer-when-downgrade"
                        src={`https://www.google.com/maps?q=${branch.latitude},${branch.longitude}&hl=${lang}&z=15&output=embed`}
                        className="w-full"
                      />
                    </div>
                  </Visibility>

                  {/* Fallback: Use address if no coordinates */}
                  <Visibility
                    visible={
                      !!(!branch.latitude || !branch.longitude) &&
                      !!(branch[addressKey as keyof BranchModel] as string)
                    }
                  >
                    <div className="mb-3 rounded-xl overflow-hidden border border-border/40">
                      <iframe
                        title={`${branch[branchNameKey as keyof BranchModel] as string} - ${t("HomePage.branchAddress")} - Map ${branch.id}`}
                        width="100%"
                        height="250"
                        style={{ border: 0 }}
                        loading="lazy"
                        allowFullScreen
                        referrerPolicy="no-referrer-when-downgrade"
                        src={`https://www.google.com/maps?q=${encodeURIComponent((branch[addressKey as keyof BranchModel] as string) || "")}&hl=${lang}&z=15&output=embed`}
                        className="w-full"
                      />
                    </div>
                  </Visibility>

                  {/* Working Hours */}
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-muted/40 border border-border/40">
                    <Clock className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-xs text-muted-foreground mb-1">
                        {t("HomePage.workingHours")}
                      </p>
                      <p
                        className="text-sm md:text-base font-medium text-foreground"
                        dir="ltr"
                      >
                        {workingHoursText}
                      </p>
                    </div>
                  </div>

                  {/* Phone */}
                  <Visibility visible={!!branch.phone}>
                    <Link
                      to={`tel:${branch.phone}`}
                      className="flex items-center gap-3 p-3 rounded-lg bg-muted/40 border border-border/40 hover:border-primary/30 hover:bg-primary/5 transition-all group/phone"
                    >
                      <Phone className="w-5 h-5 text-primary flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-xs text-muted-foreground mb-1">
                          {t("HomePage.branchPhone")}
                        </p>
                        <p
                          dir="ltr"
                          className="text-sm md:text-base font-semibold text-primary group-hover/phone:underline"
                        >
                          {branch.phone}
                        </p>
                      </div>
                    </Link>
                  </Visibility>

                  {/* Mobile */}
                  <Visibility visible={!!branch.mobile}>
                    <Link
                      to={`tel:${branch.mobile}`}
                      className="flex items-center gap-3 p-3 rounded-lg bg-muted/40 border border-border/40 hover:border-primary/30 hover:bg-primary/5 transition-all group/mobile"
                    >
                      <Smartphone className="w-5 h-5 text-primary flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-xs text-muted-foreground mb-1">
                          {t("HomePage.branchMobile")} /{" "}
                          {t("HomePage.branchWhatsapp")}
                        </p>
                        <p
                          dir="ltr"
                          className="text-sm md:text-base font-semibold text-primary group-hover/mobile:underline"
                        >
                          {branch.mobile}
                        </p>
                      </div>
                    </Link>
                  </Visibility>
                </div>
              </motion.div>
            )}
          />
        </div>
      </div>
    </section>
  );
};
